import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import * as Sentry from '@sentry/angular';

import { NetworkService } from '@core/services/network/network.service';
import { EventService } from '../services/event.service';
import { NotificationService } from '../services/notification/notification.service';

// Debounce duplicate 401 logouts when multiple concurrent requests fail
let lastLogoutTime = 0;
const LOGOUT_DEBOUNCE_MS = 2000;

/** @internal — exposed for unit tests only */
export function resetLogoutDebounce(): void {
  lastLogoutTime = 0;
}

export const errorInterceptor: HttpInterceptorFn = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const notificationService = inject(NotificationService);
  const eventService = inject(EventService);
  const network = inject(NetworkService);

  const printMessage = (errorMsg: string) => {
    if (!environment.production) {
      notificationService.presentToast(errorMsg);
    }
  };

  return next(request).pipe(
    catchError((error: unknown) => {
      if (error instanceof HttpErrorResponse) {

        Sentry.addBreadcrumb({
          category: 'http',
          message: `HTTP Error: ${error.status} ${request.method} ${request.urlWithParams}`,
          data: {
            requestId: request.headers.get('X-Request-ID'),
            status: error.status,
            statusText: error.statusText,
            online: network.isOnline(),
          },
          level: error.status === 0 ? 'warning' : 'error',
        });

        // Handle 401 immediately — no toast, no body parsing
        if (error.status === 401) {
          const now = Date.now();
          if (now - lastLogoutTime > LOGOUT_DEBOUNCE_MS) {
            lastLogoutTime = now;
            eventService.publish('user:logout');
          }
          return throwError(() => error);
        }

        let errorMessage = '';
        if (error.error && Object.prototype.hasOwnProperty.call(error.error, 'errors') && error.error.errors.length) {
          error.error.errors.forEach((errorMsg: string) => {
            printMessage(errorMsg);
          });
        } else if (error.error && Array.isArray(error.error.message)) {
          errorMessage += error.error.message[0];
          printMessage(errorMessage);
        } else {
          if (error.status === 408 || error.status === 504) {
            printMessage('Solicitud demorada, intente nuevamente.');
          } else {
            errorMessage += error.error?.message || error.message;
            printMessage(errorMessage);
          }
        }
      }
      return throwError(() => error);
    })
  );
};
